import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListOrdered, Play } from 'lucide-react';
import { useTourStore } from '../tour/tourStore';
import { NAV_ITEMS } from './GroupedNavRail';
import { navItemFor } from './routes';
import { guideToTourSteps, type AssistantGuide } from './assistantGuide';

/** Numbered "how do I" answer under an assistant message, with a button that
 *  replays it on screen through the tour overlay. */
export function AssistantGuideCard({ guide }: { guide: AssistantGuide }) {
  const { t } = useTranslation();
  const pageLabel = (page?: string) => {
    if (!page) return null;
    const item = NAV_ITEMS.find((i) => i.path === navItemFor(page));
    return item ? t(item.i18nKey, item.label) : null;
  };
  const start = () =>
    useTourStore.getState().startGuide({
      steps: guideToTourSteps(guide),
      label: guide.title,
    });

  return (
    <div data-testid="assistant-guide" style={{
      border: '1px solid var(--color-interactive-border)', borderRadius: 8,
      backgroundColor: 'var(--color-interactive-dim)', padding: '12px 14px', margin: '8px 0',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <ListOrdered size={15} color="var(--color-interactive)" />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{guide.title}</span>
      </div>
      <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {guide.steps.map((s, i) => {
          const where = pageLabel(s.page);
          return (
            <li key={i} style={{ display: 'flex', gap: 8 }}>
              <span style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                backgroundColor: 'var(--color-interactive)', color: 'white',
                fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{i + 1}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)' }}>
                  {s.title}
                  {where && (
                    <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400, color: 'var(--color-text-muted)' }}>
                      · {where}
                    </span>
                  )}
                </div>
                {s.body && (
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)', whiteSpace: 'pre-wrap' }}>{s.body}</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      <button type="button" onClick={start} data-testid="assistant-guide-start" style={{
        marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
        backgroundColor: 'var(--color-interactive)', color: 'white', fontSize: 12, fontWeight: 600,
      }}>
        <Play size={12} /> {t('shell.assistant.guide.start', 'Mostrarme en pantalla')}
      </button>
    </div>
  );
}
